import { ArrowRight, BarChart3, Check, Home, Lock, Target, Users } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import { usePlan } from '@/modules/auth';
import { permissionLabel, type AccessKey } from '@/shared/permissions';
import type { AccessReason } from '@/shared/access/use-access';

export interface UpgradeRequest {
    /** The feature that was blocked — used to label the default copy. */
    feature?:     AccessKey;
    /** Why it was blocked, so the copy can lead with the right message. */
    reason?:      AccessReason;
    /** Override the heading. */
    title?:       string;
    /** Override the body copy. */
    description?: string;
}

function featureLabel(feature?: AccessKey): string | null {
    if (!feature) return null;
    return permissionLabel(feature);
}

export function defaultUpgradeCopy(req: UpgradeRequest): { title: string; description: string } {
    const label = featureLabel(req.feature);
    const what = label ? `“${label}”` : 'this feature';
    if (req.reason === 'permission') {
        return {
            title: req.title ?? 'Access not included',
            description:
                req.description ??
                `${what} isn’t part of your current access. Upgrade your plan to unlock more of the dashboard for your team.`,
        };
    }
    return {
        title: req.title ?? 'Access not included',
        description:
            req.description ??
            `${what} isn’t available on your current plan.`,
    };
}

/** The value pitch shown on the right rail — benefit-led, not restriction-led. */
const PRO_FEATURES = [
    {
        icon:  Home,
        tile:  'bg-brand-sky-50 text-brand-sky-500',
        title: 'Unlimited Properties',
        desc:  'Add and manage unlimited property listings.',
    },
    {
        icon:  Users,
        tile:  'bg-brand-navy-50 text-brand-navy',
        title: 'Team Collaboration',
        desc:  'Invite your team and work together.',
    },
    {
        icon:  Target,
        tile:  'bg-[#FEF3E2] text-[#F59E0B]',
        title: 'Lead Management',
        desc:  'Capture, track, and manage leads efficiently.',
    },
    {
        icon:  BarChart3,
        tile:  'bg-[#E7F8EE] text-[#22C55E]',
        title: 'Advanced Reporting',
        desc:  'Gain deeper business insights.',
    },
] as const;

interface UpgradeContentProps {
    request: UpgradeRequest;
    /** Page variant drops the card shadow and fills the content area. */
    variant?: 'modal' | 'page';
    onUpgrade: () => void;
    onDismiss?: () => void;
}

/**
 * Shared upgrade pitch — the two-column layout used by the Upgrade popup and the
 * `/upgrade` page. Left rail explains the block; right rail sells Builder Pro.
 */
export function UpgradeContent({ request, variant = 'modal', onUpgrade, onDismiss }: UpgradeContentProps) {
    const plan = usePlan();
    const { title, description } = defaultUpgradeCopy(request);
    const planName = plan?.name ?? 'Free Plan';

    return (
        <div
            className={cn(
                'relative grid w-full overflow-hidden bg-white md:grid-cols-2',
                variant === 'modal'
                    ? 'max-w-4xl rounded-[24px] border border-ink-100 shadow-elevated'
                    : 'rounded-2xl border border-ink-100 shadow-soft',
            )}
        >
            {/* LEFT — the block + the ask */}
            <div className="flex flex-col gap-6 p-8 md:p-10">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-sky-50 ring-1 ring-brand-sky-100">
                    <Lock className="h-6 w-6 text-brand-sky-500" />
                </div>

                <div className="space-y-2">
                    <h1 className="font-display text-2xl font-bold leading-tight text-brand-navy">
                        {title}
                    </h1>
                    <p className="text-sm leading-relaxed text-ink-500">{description}</p>
                </div>

                <div className="rounded-2xl bg-ink-50 p-4 text-sm leading-relaxed text-ink-700">
                    Upgrade your plan to unlock more features, modules, and actions for your workspace.
                </div>

                <div className="flex items-center justify-between rounded-2xl border border-ink-100 px-4 py-3">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-300">
                            Current Plan
                        </p>
                        <p className="mt-0.5 font-display font-semibold text-brand-navy">{planName}</p>
                    </div>
                    <span className="rounded-full bg-ink-50 px-2.5 py-1 text-[11px] font-medium text-ink-500">
                        Active
                    </span>
                </div>

                <div className="mt-auto flex flex-col gap-2">
                    <button
                        type="button"
                        onClick={onUpgrade}
                        className={cn(
                            'inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white',
                            'bg-gradient-to-r from-brand-navy to-brand-sky-500 shadow-soft shadow-brand-sky-200',
                            'transition-all hover:opacity-95 hover:shadow-pop hover:shadow-brand-sky-200',
                        )}
                    >
                        Upgrade Plan
                        <ArrowRight className="h-4 w-4" />
                    </button>
                    {onDismiss && (
                        <button
                            type="button"
                            onClick={onDismiss}
                            className="rounded-xl px-5 py-2.5 text-sm font-medium text-ink-500 transition-colors hover:bg-ink-50"
                        >
                            Maybe Later
                        </button>
                    )}
                </div>
            </div>

            {/* RIGHT — the value pitch */}
            <div className="relative flex flex-col gap-6 overflow-hidden bg-gradient-to-br from-brand-navy via-brand-navy-500 to-brand-navy-600 p-8 text-white md:p-10">
                <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand-sky-400/20 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-brand-sky-500/10 blur-3xl" />

                <div className="relative space-y-1.5">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand-sky-200 ring-1 ring-white/15">
                        <Check className="h-3 w-3" /> Recommended
                    </span>
                    <h2 className="font-display text-xl font-bold">Upgrade to Builder Pro</h2>
                    <p className="text-sm text-white/70">
                        Unlock everything you need to grow your business.
                    </p>
                </div>

                <ul className="relative space-y-4">
                    {PRO_FEATURES.map(({ icon: Icon, tile, title: t, desc }) => (
                        <li key={t} className="flex items-start gap-3">
                            <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', tile)}>
                                <Icon className="h-5 w-5" />
                            </span>
                            <div className="space-y-0.5">
                                <p className="text-sm font-semibold text-white">{t}</p>
                                <p className="text-[13px] leading-snug text-white/65">{desc}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
